import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'

export interface Comment {
  _id?: string
  projectId: string
  mediaIndex: number
  userId: string
  userName: string
  userProfileImageUrl?: string
  content: string
  createdAt?: string
  updatedAt?: string
}

export const useComments = (projectId: string, mediaIndex: number) => {
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(false)

  const fetchComments = async () => {
    if (!projectId) return
    
    setLoading(true)
    try {
      const { list } = await lumi.entities.comments.list({
        filter: { projectId, mediaIndex },
        sort: { createdAt: -1 }
      })
      setComments(list)
    } catch (error) {
      console.error('Failed to fetch comments:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchComments()
  }, [projectId, mediaIndex])

  const addComment = async (commentData: Omit<Comment, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newComment = await lumi.entities.comments.create({
        ...commentData,
        creator: commentData.userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await fetchComments()
      return newComment
    } catch (error) {
      console.error('Failed to add comment:', error)
      throw error
    }
  }

  const deleteComment = async (commentId: string) => {
    if (typeof commentId !== 'string') {
      throw new Error('Comment ID must be a string')
    }
    
    try {
      await lumi.entities.comments.delete(commentId)
      await fetchComments()
    } catch (error) {
      console.error('Failed to delete comment:', error)
      throw error
    }
  }

  return {
    comments,
    loading,
    fetchComments,
    addComment,
    deleteComment
  }
}
